/**
 * Copyright: ThoughtSpot Inc. 2020
 * Author: Thawsitt Naing (thawsitt.naing@thoughtspot.com)
 *
 * @fileoverview Toast Alert for Radiant
 */
import cx from 'classnames';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Button } from '../../button';
import { Icon, IconColor, IconID, IconSize } from '../../icon';
import { Typography } from '../../typography';
import { CLOSE_BTN_ARIA_LABEL, TOAST_HIDE_DELAY_MS } from '../alert-constants';
import { AlertActionButton, AlertLink } from '../alert-interface';
import { createActionLink } from '../alert-utils';
import styles from './toast-alert.module.scss';

export interface ToastAlertProps {
  /**
   * Alert message or component to show to the user
   */
  message: string | React.ReactElement;
  /**
   * Whether the alert is visible
   */
  isVisible: boolean;
  /**
   * Show a primary button with callback
   */
  primaryAction?: AlertActionButton;
  /**
   * Show a link for the user to click
   */
  link?: AlertLink;
  /**
   * Whether or not to show the checkmark icon for alert
   *
   * @default true
   */
  showCheckmarkIcon?: boolean;
  /**
   * Whether or not to show the close button for alert
   *
   * @default true
   */
  showCloseButton?: boolean;
  /**
   * Callback function for when the user clicks on close button
   *
   * @default noop
   */
  onClose?: () => void;
  /**
   * Whether or not to auto hide the alert after a delay (4.8s)
   *
   * @default true
   */
  autoHide?: boolean;
  /**
   * Is the message a custom element
   *
   * @default false
   */
  isCustom?: boolean;
  /**
   * Any additional class name for alert container
   *
   * @default ''
   */
  className?: string;
  /**
   * Whether or not Refresh toast animation
   *
   * @default true
   */
  shouldRefreshToastAnimation?: boolean;
  /**
   * Whether or not to announce the toast alert to screen readers
   *
   * @default false
   */
  shouldAnnounceToastAlert?: boolean;
  /**
   * Ref to the toast message container for managing focus
   */
  toastMessageRef?: React.RefObject<HTMLDivElement>;
}

export const ToastAlert: React.FC<ToastAlertProps> = ({
  message,
  isVisible,
  primaryAction,
  link,
  showCheckmarkIcon = true,
  showCloseButton = true,
  onClose = _.noop,
  autoHide = true,
  isCustom = false,
  className = '',
  shouldRefreshToastAnimation = true,
  shouldAnnounceToastAlert = false,
  toastMessageRef,
  ...restProps
}: ToastAlertProps) => {
  const actionLink = link ? createActionLink(link, styles.link) : null;
  const [embedClass, setEmbedClass] = useState({});

  const primaryActionButton = primaryAction ? (
    <Button
      text={primaryAction.label}
      type="primary"
      size="s"
      backgroundType="colored"
      className={styles.primaryAction}
      onClick={primaryAction.onClick}
      data-testid="manage-alert-button"
    />
  ) : null;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (autoHide && isVisible) {
        onClose();
      }
    }, TOAST_HIDE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [autoHide, isVisible, onClose]);

  const animationClass = autoHide ? styles.autoHide : styles.noHide;
  const containerClass = cx(className, styles.container, animationClass);

  if (!isVisible || !message) {
    return null;
  }

  return (
    <div className={containerClass} {...restProps} style={embedClass}>
      {showCheckmarkIcon && (
        <Icon
          id={IconID.CHECKMARK_CIRCLE}
          size={IconSize.LARGE}
          color={IconColor.WHITE}
          className={styles.icon}
        />
      )}
      <div
        className={styles.content}
        {...(shouldAnnounceToastAlert
          ? {
              role: 'alert',
              'aria-live': 'assertive',
              tabIndex: -1,
            }
          : {})}
        ref={toastMessageRef}
      >
        {isCustom ? (
          <>{message}</>
        ) : (
          <Typography variant="body-normal" className={styles.message}>
            {message}
          </Typography>
        )}
        {actionLink}
        {primaryActionButton}
      </div>
      {showCloseButton && (
        <Button
          size="s"
          icon={IconID.CROSS}
          type="tertiary"
          backgroundType="colored"
          ariaLabel={CLOSE_BTN_ARIA_LABEL}
          onClick={onClose}
          className={styles.closeButton}
        />
      )}
    </div>
  );
};
